import { Injectable, OnDestroy } from '@angular/core';
import { defaultSortCriteria, SortCriteria } from '@app/taskpane/settings/sort-setting/sort-setting.component';
import { CommonIPStateTabType, CommonIPStateViewType, IP, UploadSubTypeGroupingMode } from '@model/ftf-api.model';
import { fromEvent, Subject, takeUntil } from 'rxjs';

export interface SettingsChange {
  previous: AddinSettings | null;
  current: AddinSettings;
}

/**
 * Represents the settings for the add-in.
 *
 * YOU MUST ADD NEW SETTINGS HERE
 */
export interface AddinSettings {
  patentParentSubType: string;
  patentAttachSubType: string;
  patentDefaultView: CommonIPStateViewType;
  patentDefaultTab: CommonIPStateTabType;
  trademarkParentSubType: string;
  trademarkAttachSubType: string;
  trademarkDefaultView: CommonIPStateViewType;
  trademarkDefaultTab: CommonIPStateTabType;
  matterParentSubType: string;
  matterAttachSubType: string;
  matterDefaultView: CommonIPStateViewType;
  matterDefaultTab: CommonIPStateTabType;
  baseUrl: string;
  regExp: string;
  regExpActive: boolean;
  appNumberMatchingEnabled: boolean;
  ipTypeDefault: string;
  autoRenameDuplicateFiles: boolean;
  navigationBarUploadOption: boolean;
  markAllAttachmentsForUpload: boolean;
  markAllEmbeddedImagesForUpload: boolean;
  includeFileExtension: boolean;
  markAsUrgent: boolean;
  autoTruncateFileName: boolean;
  autoTruncateTitleName: boolean;
  trifoldPatentDefault: IP.TrifoldEnum;
  trifoldMatterDefault: IP.TrifoldEnum;
  trifoldTrademarkDefault: IP.TrifoldEnum;
  trifoldDocumentIconAction: string;
  trifoldDocumentTextAction: string;
  defaultIpTypeUploadBar: string;
  uploadSubTypeGroupingMode: UploadSubTypeGroupingMode;
  patentSortSettings?: SortCriteria[];
  trademarkSortSettings?: SortCriteria[];
  matterSortSettings?: SortCriteria[];
}

export const BASE_URL_REGEX = /^(https?:\/\/[^/]+\/[^/]+\/).*$/i;
export function parseBaseUrl(url: string): string {
  const match = url.match(BASE_URL_REGEX);
  return match ? match[1] : '';
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService implements OnDestroy {
  protected $ngDestroyed = new Subject<void>();

  // ADD DEFAULTS FOR NEW SETTINGS HERE
  private defaults: AddinSettings = {
    baseUrl: '',
    regExp: '(?:^|\\s*\\(*)([^\\s,\\(]*-[^\\s,\\)]*)(?:$|\\s*,*\\)*)',
    regExpActive: false,
    appNumberMatchingEnabled: false,
    ipTypeDefault: 'All',
    autoRenameDuplicateFiles: false,
    navigationBarUploadOption: false,
    markAllAttachmentsForUpload: true,
    markAllEmbeddedImagesForUpload: true,
    includeFileExtension: false,
    autoTruncateFileName: false,
    autoTruncateTitleName: false,
    markAsUrgent: false,
    trifoldPatentDefault: IP.TrifoldEnum.L,
    trifoldMatterDefault: IP.TrifoldEnum.L,
    trifoldTrademarkDefault: IP.TrifoldEnum.L,
    trifoldDocumentIconAction: '3',
    trifoldDocumentTextAction: '2',
    defaultIpTypeUploadBar: '1',
    uploadSubTypeGroupingMode: UploadSubTypeGroupingMode.DocType,
    patentParentSubType: '',
    patentAttachSubType: '',
    patentDefaultView: 'trifold',
    patentDefaultTab: 'biblio',
    trademarkParentSubType: '',
    trademarkAttachSubType: '',
    trademarkDefaultView: 'trifold',
    trademarkDefaultTab: 'biblio',
    matterParentSubType: '',
    matterAttachSubType: '',
    patentSortSettings: [],
    trademarkSortSettings: [],
    matterSortSettings: [],
    matterDefaultView: 'trifold',
    matterDefaultTab: 'biblio',
  };
  private settingsKey = 'addinSettings';
  private settingsChange = new Subject<SettingsChange>();
  settingsChange$ = this.settingsChange.asObservable();

  constructor() {
    // initialize settings with defaults that may be missing at time of service creation
    const settings = this.getSettings() || {};
    // ensure localstorage defaults to having at least our defaults
    localStorage.setItem(this.settingsKey, JSON.stringify({ ...this.defaults, ...settings }));

    fromEvent<StorageEvent>(window, 'storage')
      .pipe(takeUntil(this.$ngDestroyed))
      .subscribe(() => {
        if (localStorage.getItem(this.settingsKey) === null) {
          // local storage got cleared for our settings.. set to our defaults
          this.setSettings(this.defaults);
        }
      });
  }

  ngOnDestroy(): void {
    this.$ngDestroyed.next();
    this.$ngDestroyed.complete();
  }

  getSettings(): AddinSettings {
    const settings = localStorage.getItem(this.settingsKey);
    return settings ? JSON.parse(settings) : this.defaults;
  }

  setSettings(settings: AddinSettings): void {
    const previousSettings = this.getSettings();
    localStorage.setItem(this.settingsKey, JSON.stringify(settings));
    this.settingsChange.next({ previous: previousSettings, current: settings });
  }

  resetSettings(): void {
    this.setSettings(this.defaults);
  }

  getBaseUrl(): string {
    const settings = this.getSettings();
    return settings ? settings.baseUrl : '';
  }

  setBaseUrl(baseUrl: string): void {
    const settings = this.getSettings() || {};
    settings.baseUrl = baseUrl;
    this.setSettings(settings);
  }

  setRegExp(regExp: string): void {
    const settings = this.getSettings()!;
    settings.regExp = regExp;
    this.setSettings(settings);
  }

  getAutoRenameDuplicateFiles(): boolean {
    const settings = this.getSettings();
    return settings ? settings.autoRenameDuplicateFiles : false;
  }

  getNavigationBarUploadOption(): boolean {
    const settings = this.getSettings();
    return settings ? settings.navigationBarUploadOption : false;
  }

  getUploadSubTypeGroupingMode(): UploadSubTypeGroupingMode {
    const settings = this.getSettings();
    return settings ? settings.uploadSubTypeGroupingMode : UploadSubTypeGroupingMode.DocType;
  }

  getMarkAllAttachmentsForUpload(): boolean {
    const settings = this.getSettings();
    return settings ? settings.markAllAttachmentsForUpload : true;
  }

  getIncludeFileExtension(): boolean {
    const settings = this.getSettings();
    return settings ? settings.includeFileExtension : false;
  }

  getMarkAsUrgent(): boolean {
    const settings = this.getSettings();
    return settings ? settings.markAsUrgent : false;
  }

  getAutoTruncateFileName(): boolean {
    const settings = this.getSettings();
    return settings ? settings.autoTruncateFileName : false;
  }

  getAutoTruncateTitleName(): boolean {
    const settings = this.getSettings();
    return settings ? settings.autoTruncateTitleName : false;
  }

  hasValidBaseUrl(): boolean {
    return BASE_URL_REGEX.test(this.getBaseUrl());
  }

  getParsedBaseUrl(): string {
    const baseUrl = this.getBaseUrl();
    return BASE_URL_REGEX.test(baseUrl) ? parseBaseUrl(baseUrl) : '';
  }

  getDefaultTrifoldsMap(): { [key in IP.TrifTypeEnum]: IP.TrifoldEnum } {
    const settings = this.getSettings();
    return {
      Matter: settings.trifoldMatterDefault ?? IP.TrifoldEnum.L,
      Patent: settings.trifoldPatentDefault ?? IP.TrifoldEnum.L,
      Trademark: settings.trifoldTrademarkDefault ?? IP.TrifoldEnum.L,
    };
  }

  getDefaultViewByIPType(ipType: IP.TrifTypeEnum): CommonIPStateViewType {
    const settings = this.getSettings();
    switch (ipType) {
      case IP.TrifTypeEnum.Matter:
        return settings.matterDefaultView ?? 'trifold';
      case IP.TrifTypeEnum.Patent:
        return settings.patentDefaultView ?? 'trifold';
      case IP.TrifTypeEnum.Trademark:
        return settings.trademarkDefaultView ?? 'trifold';
      default:
        return 'trifold';
    }
  }

  getDefaultTabByIPType(ipType: IP.TrifTypeEnum): string {
    const settings = this.getSettings();
    switch (ipType) {
      case IP.TrifTypeEnum.Matter:
        return settings.matterDefaultTab ?? 'biblio';
      case IP.TrifTypeEnum.Patent:
        return settings.patentDefaultTab ?? 'biblio';
      case IP.TrifTypeEnum.Trademark:
        return settings.trademarkDefaultTab ?? 'biblio';
      default:
        return 'biblio';
    }
  }

  /**
   * Generates a local storage key based on the current URL and a salt.
   * salt will affect the key, but the key will be the same for the same URL and salt.
   * this is not a replacement for encryption, but a way to generate a key that is unique to the URL and salt.
   * do not use this for security purposes.
   * @param salts - The salts to use in generating the key.
   */
  generateHexKey(...salts: string[]): string {
    const isLocalhost = window.location.hostname === 'localhost';
    if (isLocalhost) {
      return salts.reverse().join('.');
    }
    const rawText: string = salts.join('');
    const positionMultiplier = 1000;
    const encoder: TextEncoder = new TextEncoder();
    const encodedText: Uint8Array = encoder.encode(rawText);
    const radix = 16;
    const MAX_24_BIT_NUMBER = 0xffffff;

    const txtToNum: number = encodedText.reduce((sum: number, charCode: number, index: number) => {
      return sum + charCode + index * positionMultiplier;
    }, 0);

    const number: number = txtToNum & MAX_24_BIT_NUMBER;
    const key: string = number.toString(radix).toUpperCase();
    return key;
  }

  getPatentSortSettings(): SortCriteria[] {
    const settings = this.getSettings();
    return settings?.patentSortSettings || [...defaultSortCriteria];
  }

  getTrademarkSortSettings(): SortCriteria[] {
    const settings = this.getSettings();
    return settings?.trademarkSortSettings || [...defaultSortCriteria];
  }

  getMatterSortSettings(): SortCriteria[] {
    const settings = this.getSettings();
    return settings?.matterSortSettings || [...defaultSortCriteria];
  }
}
